import React, { useState } from 'react';

import { get } from 'lodash';
import cx from 'classnames';
import { useNavigate } from 'react-router-dom';
import { CodeResponse, useGoogleLogin } from '@react-oauth/google';

import axios from '@/axios';
import Icons from '@/icons';
import { iconConstants, routesConstants } from '@/constants';
import { generalHelpers } from '@/helpers';
import { EmailConfirmation } from '@/component';
import { Button, Input } from '@/kaya';
import { songActions, userActions } from '@/actions';
import GoogleButton from '@/component/googleButton/GoogleButton';
import { songsStore, useArtistStore } from '@/store';
import logo from '@/assets/upperLogin-image.png';

import styles from './Register.module.scss';

const Register = () => {
  const navigate = useNavigate();

  const { setArtist, setAuthentication, defaultArtist } = useArtistStore();
  const { setSongs } = songsStore();

  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [err, setErr] = useState<{ [key: string]: string }>({
    username: '',
    password: '',
    confirmPassword: '',
  });
  const [googlLogin, setGoogleLogin] = useState(false);
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleEyeClick = () => {
    setShowPassword(!showPassword);
  };

  const handleConfirmEyeClick = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleRegister = async () => {
    const error = {
      email: generalHelpers.validateEmail(email),
      password: generalHelpers.validatePassword(password),
      confirmPassword: generalHelpers.validateConfirmPassword(password, confirmPassword),
    };
    if (!generalHelpers.isEmptyErrors(error)) {
      setErr(error);
      return;
    }
    // write api call here
    setLoading(true);
    const isLoginSuccess = await userActions.registerAction({
      payload: { email, password, rePassword: confirmPassword },
    });

    if (isLoginSuccess) {
      setVisible(true);
      // navigate(routesConstants.ROUTES.PERSONAL_INFO);
      setLoading(false);
    }
    setLoading(false);
  };

  const onSuccess = async (res: CodeResponse) => {
    try {
      setGoogleLogin(true);
      const { code } = res;

      const data = await userActions.googleLoginAction(code);
      if (!data.success) return;

      setArtist({
        ...defaultArtist,
        id: get(data, 'data.id', {}),
      });

      localStorage.setItem('refresh_token', get(data, 'data.refresh_token', ''));

      axios.defaults.headers.common['Authorization'] = `Bearer ${get(
        data,
        'data.access_token',
        '',
      )}`;

      const artistData = await userActions.fetchArtistAction(get(data, 'data.id', ''));

      if (get(artistData, 'data.code') === 'PHONE_NOT_VERIFIED') {
        setAuthentication({
          isAuthenticated: true,
          isPhoneVerified: false,
        });
        navigate(routesConstants.ROUTES.PERSONAL_INFO);
        return;
      } else {
        const songsData = await songActions.fetchSongsAction(get(data, 'data.id', {}));

        if (songsData.success) {
          setSongs(get(songsData, 'data', []));
        }
        setAuthentication({
          isAuthenticated: true,
          isPhoneVerified: true,
        });
        setArtist(get(artistData, 'data', {}));
        navigate(routesConstants.ROUTES.HOME);
      }
    } catch (error: any) {
      generalHelpers.toaster('error', error.message || 'Something went wrong');
    } finally {
      setGoogleLogin(false);
    }
  };

  const onError = (err: Pick<CodeResponse, 'error_description'>) => {
    const { error_description } = err;
    generalHelpers.toaster('error', error_description || 'Something went wrong');
  };

  const googleLogin = useGoogleLogin({
    flow: 'auth-code',
    onSuccess,
    onError,
    //scope: only
  });

  const handleLogin = () => {
    navigate(routesConstants.ROUTES.LOGIN);
  };

  return (
    <>
      <div className={cx(styles.Register, { [styles.visible]: visible })}>
        <div className={styles.logo}>
          <img src={logo} alt="logo" className={styles.logoImg} />
        </div>
        <div className={styles.boxLogin}>
          <h1 className={styles.title}>Create an account</h1>
          <p className={styles.title2}>Welcome to For Play </p>

          <div className={styles.box}>
            <Input
              type="email"
              placeholder="Email"
              width="100%"
              onChange={(e) => {
                setEmail(e);
                setErr({
                  ...err,
                  ['email']: '',
                });
              }}
              errors={err}
              id="email"
            />

            <Input
              type={showPassword ? 'string' : 'password'}
              placeholder="Password"
              width="100%"
              onChange={(e) => {
                setPassword(e);
                setErr({
                  ...err,
                  ['password']: '',
                });
              }}
              errors={err}
              id="password"
              suffixIcon={
                <Icons
                  type={showPassword ? iconConstants.ICONS.EYEOFF : iconConstants.ICONS.EYEON}
                />
              }
              onSuffixIconClick={handleEyeClick}
            />
            <Input
              type={showConfirmPassword ? 'string' : 'password'}
              placeholder="Confirm Password"
              containerClassName={styles.input3}
              onChange={(e) => {
                setConfirmPassword(e);
                setErr({
                  ...err,
                  ['confirmPassword']: '',
                });
              }}
              errors={err}
              id="confirmPassword"
              onEnterClick={handleRegister}
              suffixIcon={
                <Icons
                  type={
                    showConfirmPassword ? iconConstants.ICONS.EYEOFF : iconConstants.ICONS.EYEON
                  }
                />
              }
              onSuffixIconClick={handleConfirmEyeClick}
            />

            <Button
              className={styles.btn1}
              onClick={handleRegister}
              isLoading={loading}
              type="Primary"
            >
              Sign Up
            </Button>
            <GoogleButton className={styles.btn2} onClick={googleLogin} isLoading={googlLogin} />
            <div className={styles.account}>
              <div className={styles.line1}></div>
              <p className={styles.text2}>Already have an account?</p>
              <div className={styles.line2}></div>
            </div>
            <Button className={styles.btn3} onClick={handleLogin} type="Outline">
              Sign in
            </Button>
          </div>
          <p className={styles.text}>
            Signing Up on For Play means you agree to the{' '}
            <a className={styles.text1}>Privacy Policy</a> and
            <a className={styles.text1} href={routesConstants.ROUTES.TnC}>
              Terms of Service
            </a>
          </p>
        </div>
      </div>
      <EmailConfirmation
        visible={visible}
        setVisible={() => {
          setVisible(!visible);
          navigate(routesConstants.ROUTES.LOGIN);
        }}
        email={email}
      />
    </>
  );
};

export default Register;
